import type { MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import type { LoaderFunctionArgs } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { getAuthUser } from "~/services/auth.server";

export const meta: MetaFunction = () => {
  return [
    { title: "개인정보보호정책 - 슬로우다이브" },
    { name: "description", content: "슬로우다이브 개인정보보호정책입니다." },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getAuthUser(request);

  return json({
    user,
  });
}

export default function PrivacyPage() {
  const { user } = useLoaderData<typeof loader>();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex mb-8" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
            <li>
              <a
                href="/"
                className="hover:text-gray-700 dark:hover:text-gray-300"
              >
                홈
              </a>
            </li>
            <li>
              <span className="mx-2">/</span>
            </li>
            <li className="text-gray-900 dark:text-white font-medium">
              개인정보보호정책
            </li>
          </ol>
        </nav>

        {/* Main content */}
        <article className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          <div className="p-8">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
                슬로우다이브 개인정보보호정책
              </h1>

              {/* Highlight box */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-6 mb-8">
                <p className="text-blue-800 dark:text-blue-200 font-medium">
                  주식회사 슬로우다이브(이하 '회사')는 이용자의 개인정보를
                  중요시하며, 「정보통신망 이용촉진 및 정보보호 등에 관한
                  법률」, 「개인정보 보호법」을 준수하기 위하여 노력하고
                  있습니다.
                </p>
              </div>

              <div className="space-y-8 text-gray-800 dark:text-gray-200">
                {/* 1. 수집하는 개인정보 항목 및 수집방법 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    1. 수집하는 개인정보 항목 및 수집방법
                  </h2>

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    가. 수집하는 개인정보의 항목
                  </h3>
                  <p className="mb-4">
                    회사는 회원가입, 상담, 서비스 신청 등을 위해 아래와 같은
                    개인정보를 수집하고 있습니다.
                  </p>
                  <ul className="list-disc list-inside space-y-2 mb-6">
                    <li>필수항목: 이름, 이메일 주소, 휴대전화번호, 비밀번호</li>
                    <li>
                      선택항목: 프로필 이미지, 직업, 관심분야, 서비스 이용 기록,
                      접속 로그
                    </li>
                    <li>
                      결제 시 수집항목: 신용카드 정보, 은행계좌정보, 결제기록 등
                      결제정보(※ 회사는 결제정보를 저장하지 않으며, PG사에서
                      처리됩니다)
                    </li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    나. 개인정보 수집방법
                  </h3>
                  <p className="mb-4">
                    회사는 다음과 같은 방법으로 개인정보를 수집합니다.
                  </p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>홈페이지 회원가입, 서비스 이용, 이벤트 참여</li>
                    <li>생성정보 수집 툴을 통한 자동 수집</li>
                  </ul>
                </section>

                {/* 2. 개인정보의 수집 및 이용목적 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    2. 개인정보의 수집 및 이용목적
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-700">
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                            구분
                          </th>
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                            이용목적
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 font-medium">
                            회원 관리
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            회원제 서비스 이용, 개인식별, 불량회원의 부정이용
                            방지와 비인가 사용 방지, 가입의사 확인, 가입 및
                            가입횟수 제한, 분쟁 조정을 위한 기록보존, 불만처리
                            등 민원처리, 고지사항 전달
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 font-medium">
                            서비스 제공
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            식단 및 건강 콘텐츠 제공, 결제 정보 관리, 맞춤형
                            콘텐츠 제공
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 font-medium">
                            마케팅 및 광고
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            신규 서비스 개발 및 맞춤 서비스 제공, 이벤트 및
                            광고성 정보 제공 (마케팅 정보 수신에 동의한 경우에
                            한함)
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* 3. 개인정보의 보유 및 이용기간 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    3. 개인정보의 보유 및 이용기간
                  </h2>
                  <p className="mb-4">
                    회사는 원칙적으로 개인정보 수집 및 이용목적이 달성된 후에는
                    해당 정보를 지체 없이 파기합니다. 단, 다음의 정보에 대해서는
                    아래의 이유로 명시한 기간 동안 보존합니다.
                  </p>

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    가. 회사 내부 방침에 의한 정보 보유
                  </h3>
                  <ul className="list-disc list-inside space-y-2 mb-6">
                    <li>회원 탈퇴 시 개인정보: 탈퇴일로부터 30일</li>
                    <li>
                      이유: 서비스 이용의 불편함 방지, 재가입 기간 제한,
                      분쟁해결 및 수사 협조
                    </li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    나. 관련 법령에 의한 정보 보유
                  </h3>
                  <ul className="list-disc list-inside space-y-2">
                    <li>
                      계약 또는 청약철회 등에 관한 기록: 5년 (전자상거래
                      등에서의 소비자보호에 관한 법률)
                    </li>
                    <li>
                      대금결제 및 재화 등의 공급에 관한 기록: 5년 (전자상거래
                      등에서의 소비자보호에 관한 법률)
                    </li>
                    <li>
                      소비자의 불만 또는 분쟁처리에 관한 기록: 3년 (전자상거래
                      등에서의 소비자보호에 관한 법률)
                    </li>
                    <li>웹사이트 방문 기록: 3개월 (통신비밀보호법)</li>
                  </ul>
                </section>

                {/* 4. 개인정보의 파기절차 및 방법 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    4. 개인정보의 파기절차 및 방법
                  </h2>
                  <p className="mb-4">
                    회사는 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가
                    불필요하게 되었을 때에는 지체없이 해당 개인정보를
                    파기합니다.
                  </p>

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    가. 파기절차
                  </h3>
                  <p className="mb-4">
                    이용자가 입력한 정보는 목적 달성 후 내부 방침 및 관련 법령에
                    따라 일정기간 저장된 후 혹은 즉시 파기됩니다.
                  </p>

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    나. 파기방법
                  </h3>
                  <p>
                    전자적 파일 형태로 저장된 개인정보는 기록을 재생할 수 없는
                    기술적 방법을 사용하여 삭제합니다. 종이에 출력된 개인정보는
                    분쇄기로 분쇄하거나 소각을 통하여 파기합니다.
                  </p>
                </section>

                {/* 5. 개인정보 제3자 제공 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    5. 개인정보 제3자 제공
                  </h2>
                  <p className="mb-4">
                    회사는 이용자의 개인정보를 원칙적으로 외부에 제공하지
                    않습니다. 다만, 아래의 경우에는 예외로 합니다.
                  </p>
                  <ul className="list-disc list-inside space-y-2 mb-6">
                    <li>이용자가 사전에 동의한 경우</li>
                    <li>
                      법령의 규정에 의거하거나, 수사 목적으로 법령에 정해진
                      절차와 방법에 따라 수사기관의 요구가 있는 경우
                    </li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    개인정보 제3자 제공 내역
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-700">
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                            제공받는 자
                          </th>
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                            제공목적
                          </th>
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                            제공정보
                          </th>
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                            보유 및 이용기간
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            결제대행사 (KG이니시스)
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            상품 구매에 따른 결제 처리
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            구매자명, 연락처, 이메일, 결제정보
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            이용목적 달성 시까지
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            배송업체 (우체국택배)
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            상품(워크북, 교재) 배송
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            수령인 성명, 주소, 연락처
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            배송 완료 시까지
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* 6. 이용자 및 법정대리인의 권리와 그 행사방법 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    6. 이용자 및 법정대리인의 권리와 그 행사방법
                  </h2>
                  <p className="mb-4">
                    이용자 및 법정 대리인은 언제든지 등록되어 있는 자신 혹은
                    당해 만 14세 미만 아동의 개인정보를 조회하거나 수정할 수
                    있으며, 회사의 개인정보 처리에 동의하지 않는 경우 동의를
                    거부하거나 가입해지(회원탈퇴)를 요청할 수 있습니다.
                  </p>
                  <p>
                    이용자의 개인정보 조회, 수정을 위해서는 '개인정보변경'(또는
                    '회원정보수정' 등)을, 가입해지(동의철회)를 위해서는
                    '회원탈퇴'를 클릭하여 본인 확인 절차를 거치신 후 직접 열람,
                    정정 또는 탈퇴가 가능합니다.
                  </p>
                </section>

                {/* 7. 개인정보 자동 수집 장치의 설치/운영 및 거부에 관한 사항 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    7. 개인정보 자동 수집 장치의 설치/운영 및 거부에 관한 사항
                  </h2>
                  <p className="mb-4">
                    회사는 이용자의 정보를 수시로 저장하고 찾아내는
                    '쿠키(cookie)'를 사용합니다. 쿠키는 웹사이트가 이용자의
                    컴퓨터 브라우저로 전송하는 소량의 정보입니다.
                  </p>

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    가. 쿠키의 사용목적
                  </h3>
                  <p className="mb-4">
                    이용자의 접속 빈도나 방문 시간 등을 분석하고 이용자의 취향과
                    관심분야를 파악하여 서비스 개선에 활용합니다.
                  </p>

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    나. 쿠키 설정 거부 방법
                  </h3>
                  <p>
                    이용자는 쿠키 설치에 대한 선택권을 가지고 있습니다. 따라서
                    웹브라우저에서 옵션을 설정함으로써 모든 쿠키를 허용하거나,
                    쿠키가 저장될 때마다 확인을 거치거나, 아니면 모든 쿠키의
                    저장을 거부할 수도 있습니다.
                  </p>
                </section>

                {/* 8. 개인정보의 기술적/관리적 보호 대책 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    8. 개인정보의 기술적/관리적 보호 대책
                  </h2>
                  <p className="mb-4">
                    회사는 이용자의 개인정보를 취급함에 있어 개인정보가 분실,
                    도난, 누출, 변조 또는 훼손되지 않도록 안전성 확보를 위하여
                    다음과 같은 기술적, 관리적 대책을 강구하고 있습니다.
                  </p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>
                      개인정보 암호화: 이용자의 비밀번호와 같은 중요 정보는
                      암호화하여 저장 및 관리
                    </li>
                    <li>
                      해킹 등에 대비한 대책: 해킹이나 컴퓨터 바이러스 등에 의해
                      개인정보가 유출되거나 훼손되는 것을 막기 위해
                      보안프로그램을 설치하고 주기적인 갱신·점검을 시행
                    </li>
                    <li>
                      개인정보에 대한 접근 제한: 개인정보를 처리하는
                      데이터베이스시스템에 대한 접근권한의 부여, 변경, 말소를
                      통하여 개인정보에 대한 접근통제를 위하여 필요한 조치를
                      하고 있으며 침입차단시스템을 이용하여 외부로부터의 무단
                      접근을 통제
                    </li>
                    <li>
                      개인정보 취급 직원의 최소화 및 교육: 개인정보를 취급하는
                      직원을 최소화하여 지정하고 정기적인 교육을 시행
                    </li>
                  </ul>
                </section>

                {/* 9. 개인정보 보호책임자 안내 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    9. 개인정보 보호책임자 안내
                  </h2>
                  <p className="mb-4">
                    회사는 개인정보 처리에 관한 업무를 총괄해서 책임지고,
                    개인정보 처리와 관련한 정보주체의 불만처리 및 피해구제 등을
                    위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.
                  </p>
                  <div className="overflow-x-auto mb-4">
                    <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-700">
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                            구분
                          </th>
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                            개인정보 보호책임자
                          </th>
                          <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                            개인정보 보호 담당부서
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            이름
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            김영훈
                          </td>
                          <td
                            className="border border-gray-300 dark:border-gray-600 px-4 py-3"
                            rowSpan={3}
                          >
                            고객지원팀
                            <br />
                            이메일: privacy@slowdive.kr
                            <br />
                            전화번호: 02-123-4567
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            직위
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            개인정보보호책임자
                          </td>
                        </tr>
                        <tr>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            연락처
                          </td>
                          <td className="border border-gray-300 dark:border-gray-600 px-4 py-3">
                            02-123-4567
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p>
                    이용자는 회사의 서비스를 이용하면서 발생한 모든 개인정보
                    보호 관련 문의, 불만처리, 피해구제 등에 관한 사항을 개인정보
                    보호책임자 및 담당부서로 문의할 수 있습니다. 회사는
                    정보주체의 문의에 대해 지체 없이 답변 및 처리해드릴
                    것입니다.
                  </p>
                </section>

                {/* 10. 개인정보처리방침의 변경 */}
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    10. 개인정보처리방침의 변경
                  </h2>
                  <p className="mb-4">
                    이 개인정보처리방침은 시행일로부터 적용되며, 법령 및 방침에
                    따른 변경내용의 추가, 삭제 및 정정이 있는 경우에는
                    변경사항의 시행 7일 전부터 공지사항을 통하여 고지할
                    것입니다.
                  </p>
                  <p>시행일: 2025년 8월 1일</p>
                </section>
              </div>

              {/* Footer */}
              {/*  <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  © 2025 슬로우다이브 | 서울시 강남구 강남대로 123, 4층
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  사업자등록번호: 123-45-67890 | 대표자: 홍길동
                </p>
              </div> */}
              <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  © 2025 slowdive | All rights reserved.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  본 개인정보보호정책은 당사의 서비스를 이용하는 모든 이용자에게
                  적용됩니다.
                </p>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
